import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../utils";

import { useTranslation } from "react-i18next";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import PackageTable from "../../../components/Tables/PackageTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setDetailIds } from "../../../lib/slices/detailIdsSlice";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import {
  setUserPackageCurrentPage,
  setUserPackagePageLimit,
  setUserPackageTotalCount,
  setUserPackageTotalPages,
} from "../../../lib/slices/userPackageSlice";
import {
  deleteCustomPackage,
  exportCustomPackages,
  getAllCustomPackages,
  updateCustomPackageByUrl,
} from "../../../services/userPackageFn";
import { deleteConfirmAlert } from "../../../components/Alert";
import { CSVLink } from "react-csv";
import moment from "moment";
import {
  CURRENCY,
  DEFAULT_VAT_PERCENTAGE,
  PRODUCT_USAGE_TYPE,
} from "../../../constants/Variables";
import GlobalButton from "../../../components/CustomButton";

const CustomPackagesList = ({ handleChangeTab }) => {
  const csvLinkRef = useRef(null);
  const dispatch = useDispatch();
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [exportLoading, setExportLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const { t } = useTranslation(["products", "common"]);

  const userData = useSelector((state) => state.userData.user);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const detailIds = useSelector((state) => state.detailIdReducer.detailIds);
  const pagination = useSelector((state) => state.userPackagePaginationReducer);
  const settings = useSelector((state) => state.settingsReducer.settings);
  const debouncedSearchTerm = useDebounce(
    searchKeys.userPackageName,
    1000,
    true,
    () => {
      dispatch(setUserPackageCurrentPage(1));
    },
  );

  const canEditDelete =
    userData?.adminId === (searchKeys.vendorsForPackage || userData?.adminId);

  const headerData = [
    "createdAt",
    "createdBy",
    "nameImage",
    "solutionType",
    "category",
    "subCategory",
    "subSubCategory",
    "packagePrice",
    "packageEffectivePrice",
    "vat",
    "packageVatAmount",
    "packagePriceAfterVat",
    "orderAllowed",
    "totalOrders",
    "rating",
    "verified",
    "status",
  ];
  const tableRows = [
    "created_date",
    "custom_package_created_by",
    "name_image",
    "solution_type",
    "category_name",
    "sub_category_name",
    "sub_sub_category_name",
    "package_price",
    "package_effective_price",
    "app_vat",
    "package_vat_amount",
    "package_price_after_vat",
    "order_allowed",
    "totalOrders",
    "rating_in_number",
    "verified",
    "status",
    // "actions",
  ];

  const fetchchedAllProducts = async (search, page, limit) => {
    let query = {
      search,
      page,
      limit,
    };

    try {
      setIsLoading(true);
      const response = await getAllCustomPackages(query);
      if (response?.data?.status === "success") {
        const pagination = response?.data?.data?.pagination;
        setAllProducts(response?.data?.data?.data);
        dispatch(setUserPackagePageLimit(pagination.limit));
        dispatch(setUserPackageCurrentPage(pagination.page));
        dispatch(setUserPackageTotalCount(pagination.total));
        dispatch(setUserPackageTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchchedAllProducts(
        debouncedSearchTerm,
        pagination.page,
        pagination.limit,
      );
    }
  }, [userData, debouncedSearchTerm, pagination.page, pagination.limit]);

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setUserPackageCurrentPage(page));
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setUserPackagePageLimit(limit));
    dispatch(setUserPackageCurrentPage(1));
  };

  const handleEditProduct = (row) => {
    handleChangeTab("user_package_details");
    dispatch(setDetailIds({ ...detailIds, userPackageId: row?._id }));
  };

  const handleChangeStatus = async (e, row, key = "status") => {
    try {
      setIsLoading(true);
      const response = await updateCustomPackageByUrl(row?.mlSlug?.en, {
        [key]: e,
      });
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        fetchchedAllProducts(
          debouncedSearchTerm,
          pagination.page,
          pagination.limit,
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCustomPackage = async (row) => {
    deleteConfirmAlert({
      title: t("deleteConfirmation", { ns: "common" }),
      text: t("deleteConfirmationText", { ns: "common" }),
      confirmButtonText: t("delete", { ns: "common" }),
      cancelButtonText: t("cancel", { ns: "common" }),
    }).then(async (result) => {
      if (result) {
        try {
          setIsLoading(true);
          const response = await deleteCustomPackage(row?._id);
          if (response?.data?.status === "success") {
            fetchchedAllProducts(
              debouncedSearchTerm,
              pagination.page,
              pagination.limit,
            );
            handleOpenSnackBar("success", response?.data?.message);
          } else {
            handleOpenSnackBar("error", response?.data?.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const renderSearchAction = () => {
    return (
      <Stack
        gap={1}
        p={2}
        direction={{ md: "row" }}
        bgcolor={"primary.light"}
        alignItems={"center"}
      >
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.userPackageName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      userPackageName: "",
                    }),
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.userPackageName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                userPackageName: e.target.value,
              }),
            );
          }}
        />
      </Stack>
    );
  };

  // Export data for base packages
  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const response = await exportCustomPackages();
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];
        data.forEach((item) => {
          // This is calculated for the base packages
          const finalPackagePrice = item.effectivePackagePrice;

          // This is calculated for the base packages
          const packageVat = calculateVat(
            finalPackagePrice,
            settings?.tax || DEFAULT_VAT_PERCENTAGE,
          );

          // This is calculated for the base packages
          const finalPriceAfterVat = roundTo2Decimal(
            finalPackagePrice + packageVat,
          );
          formattedData.push({
            "Created By (Name)":
              `${item.user?.firstname || ""} ${item.user?.lastname || ""}`.trim() ||
              "-",
            "Created By (Email)": item.user?.email || "-",
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Object Id": item._id || "-",
            "Slug In (Ro)": item.mlSlug?.ro || "-",
            "Slug In (En)": item.mlSlug?.en || "-",
            "Cover Image": item.coverImage || "-",
            Images: item.images?.map((image) => image).join(", ") || "-",
            "Name In (Ro)": item.name?.ro || "-",
            "Name In (En)": item.name?.en || "-",
            "Solution Type (Ro)":
              item.solutionType
                ?.map(
                  (type) =>
                    PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label?.ro,
                )
                .filter(Boolean)
                .join(", ") || "-",
            "Solution Type (En)":
              item.solutionType
                ?.map(
                  (type) =>
                    PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label?.en,
                )
                .filter(Boolean)
                .join(", ") || "-",
            "Category (Ro)": item.category?.name?.ro || "-",
            "Category (En)": item.category?.name?.en || "-",
            "Sub Category (Ro)":
              item.subCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
            "Sub Category (En)":
              item.subCategory?.map((sub) => sub.name?.en).join(", ") || "-",
            "Sub Sub Category (Ro)":
              item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
            "Sub Sub Category (En)":
              item.subSubCategory?.map((sub) => sub.name?.en).join(", ") || "-",
            Price: `${item.packagePrice.toFixed(2)} ${CURRENCY}`,
            "Effective Price": `${item.effectivePackagePrice.toFixed(2)} ${CURRENCY}`,
            VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
            "VAT Amount": `${packageVat.toFixed(2)} ${CURRENCY}`,
            "Price After VAT": `${finalPriceAfterVat.toFixed(2)} ${CURRENCY}`,
            "Order Allowed": item.isAllowedOrder ? "Yes" : "No",
            "Total Orders": item.totalOrders,
            Status: item.status ? "Active" : "Inactive",
            Verified: item.isVerified ? "Yes" : "No",
            Rating: item.rating?.toFixed(0) || "-",
          });
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={{ sm: "row" }}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"custom_packages.csv"}
          className="csv-link"
          target="_blank"
          style={{ display: "none" }}
        />

        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          disabled={exportLoading}
          handleClickBtn={prepareExportData}
        />
      </Stack>
    );
  };

  const allProductsData = useMemo(() => {
    return (
      <PackageTable
        minWidth={2800}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allProducts}
        paginatedData={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        showEdit={true}
        showDelete={true}
        handleEdit={handleEditProduct}
        handleDelete={handleDeleteCustomPackage}
        handleChangeStatus={(e, row) => handleChangeStatus(e, row, "status")}
        handleUpdateVerification={(e, row) =>
          handleChangeStatus(e, row, "isVerified")
        }
        // showActions={true}
      />
    );
  }, [allProducts]);

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("customPackagesList")}
        subtitle={t("customPackagesListInstruction")}
        searchActions={renderSearchAction()}
        headerActions={renderHeaderAction()}
      >
        {allProductsData}
        {allProducts?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}!
          </Typography>
        )}
      </PaperContainer>
    </>
  );
};

export default CustomPackagesList;
